import { onlineManager } from '@tanstack/react-query'
import { ApiError, setSuppress401Redirect } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'

let draining = false

// An outbox mutation key starts with 'outbox' (see lib/queryClient.ts).
export function isOutboxKey(mutationKey: unknown): boolean {
  return Array.isArray(mutationKey) && mutationKey[0] === 'outbox'
}

// Transient failures are worth auto-retrying on the next drain: network-level
// errors (fetch threw before a response — not ApiError) and 5xx gateway /
// server errors. 4xx responses are real rejections (validation, conflicts)
// and stay failed for manual attention in the pending-changes drawer.
function isTransientError(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500
  return true
}

// Replay paused (offline-queued) mutations sequentially in submission order,
// then retry any that failed transiently (e.g. a 502 from the proxy while the
// backend was down — with the SPA topology a "network" failure often arrives
// as an HTTP error instead of a fetch throw). Individual failures settle into
// that mutation's error state and surface in the pending-changes drawer
// instead of aborting the drain.
async function drainOutbox(): Promise<void> {
  if (draining) return
  draining = true
  // A 401 during replay means the session expired while offline. Redirecting
  // to /login mid-drain would lose the queue UI — let the failures land in
  // the drawer and prompt re-login there.
  setSuppress401Redirect(true)
  try {
    await queryClient.resumePausedMutations()

    const retryable = queryClient
      .getMutationCache()
      .getAll()
      .filter(
        (m) =>
          m.state.status === 'error' &&
          isOutboxKey(m.options.mutationKey) &&
          isTransientError(m.state.error),
      )
    for (const m of retryable) {
      try {
        await m.execute(m.state.variables)
      } catch {
        // stays in error state; surfaced in the drawer
      }
    }

    // A replay can affect any query key — refetch everything once, cheap at
    // this app's scale.
    await queryClient.invalidateQueries()
  } finally {
    setSuppress401Redirect(false)
    draining = false
  }
}

export function initOfflineSync(): void {
  // Resume whatever was restored from the previous session (no-op when the
  // app is still offline — mutations stay paused).
  void drainOutbox()
  // onlineManager tracks navigator online/offline events by default.
  onlineManager.subscribe(() => {
    if (onlineManager.isOnline()) void drainOutbox()
  })
}
